import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import type { FloatingPosition, FloatingSize } from './AgentChatContext'
import ChatWindowHeader from './ChatWindowHeader'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './DraggableResizableWindow.module.scss'

interface DraggableResizableWindowProps {
  children: React.ReactNode
  onClose: () => void
  onPositionChange: (position: FloatingPosition) => void
  onSizeChange: (size: FloatingSize) => void
  onToggleMode?: () => void
  position: FloatingPosition
  size: FloatingSize
}

const DraggableResizableWindow: React.FC<DraggableResizableWindowProps> = ({
  children,
  position,
  size,
  onPositionChange,
  onSizeChange,
  onClose,
  onToggleMode,
}) => {
  const { t } = useTranslation('Agent')
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  })

  const windowRef = useRef<HTMLDivElement>(null)

  const constrainPosition = useCallback(
    (pos: FloatingPosition): FloatingPosition => {
      const minX = 0
      const maxX = window.innerWidth - size.width
      const minY = 0
      const maxY = window.innerHeight - size.height

      return {
        x: Math.max(minX, Math.min(pos.x, maxX)),
        y: Math.max(minY, Math.min(pos.y, maxY)),
      }
    },
    [size.width, size.height],
  )

  const constrainSize = useCallback(
    (newSize: FloatingSize): FloatingSize => {
      const minWidth = 350
      const maxWidth = window.innerWidth - position.x
      const minHeight = 310
      const maxHeight = window.innerHeight - position.y

      return {
        width: Math.max(minWidth, Math.min(newSize.width, maxWidth)),
        height: Math.max(minHeight, Math.min(newSize.height, maxHeight)),
      }
    },
    [position.x, position.y],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      })
      e.preventDefault()
    },
    [position.x, position.y],
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const newPosition = constrainPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        })
        onPositionChange(newPosition)
      } else if (isResizing) {
        const newSize = constrainSize({
          width: resizeStart.width + (e.clientX - resizeStart.x),
          height: resizeStart.height + (e.clientY - resizeStart.y),
        })
        onSizeChange(newSize)
      }
    },
    [
      isDragging,
      dragStart,
      isResizing,
      resizeStart,
      constrainPosition,
      constrainSize,
      onPositionChange,
      onSizeChange,
    ],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setIsResizing(false)
  }, [])

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      setIsResizing(true)
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height,
      })
      e.preventDefault()
      e.stopPropagation()
    },
    [size.width, size.height],
  )

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
    return undefined
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp])

  return (
    <div
      className={classNames(styles.floatingWindow, {
        [styles.dragging]: isDragging,
        [styles.resizing]: isResizing,
      })}
      ref={windowRef}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
      }}
    >
      {/* Window header with drag handle and controls */}
      <ChatWindowHeader
        draggable
        isFloating
        title={t('Agent:AI_ASSISTANT')}
        onClose={onClose}
        onMouseDown={handleMouseDown}
        onToggleMode={onToggleMode}
      />

      {/* Resize handle */}
      <div
        aria-label={t('RESIZE_WINDOW')}
        className={styles.resizeHandle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            // Resize handle doesn't have a meaningful action for keyboard users
            // The resize functionality is mouse-only
          }
        }}
        onMouseDown={handleResizeStart}
      />

      {/* Window content */}
      <div className={styles.windowContent}>{children}</div>
    </div>
  )
}

export default DraggableResizableWindow
